import { appendPrintNote } from './printNotes';
import { formatCommandItem, formatCourseHeading, formatCourseSeparator } from './printOrderLayout';

const uniqueStrings = (values) => [...new Set((Array.isArray(values) ? values : [])
  .map((value) => String(value || '').trim())
  .filter(Boolean))];

export const normalizeOrderPrintFontSize = (value) => {
  const normalized = String(value || 'NORMAL').trim().toUpperCase();
  return ['SMALL', 'NORMAL', 'LARGE'].includes(normalized) ? normalized : 'NORMAL';
};

const printableCustomer = (value) => {
  const label = String(value || '').trim();
  if (!label || /^nessun cliente/i.test(label) || /^[-–—]+$/.test(label)) return '';
  return label;
};

export const resolveProductPrinterTargets = (product, roomId) => {
  const normalizedRoomId = Number(roomId);
  const roomOverride = (Array.isArray(product?.roomPrinterTargets) ? product.roomPrinterTargets : [])
    .find((entry) => Number(entry?.roomId) === normalizedRoomId);
  const overrideTargets = uniqueStrings(roomOverride?.printerTargets);
  return overrideTargets.length > 0 ? overrideTargets : uniqueStrings(product?.printerTargets);
};
const isTastingMenu = (row) => row?.package?.type === 'TASTING_MENU' || row?.packageType === 'TASTING_MENU';
const packageItems = (row) => {
  if (Array.isArray(row?.items) && row.items.length > 0) return row.items;
  return Array.isArray(row?.package?.items) ? row.package.items : [];
};

const packageDefinitionProduct = (row, item) => (
  item?.product
  || (row?.package?.items || []).find((definition) => (
    Number(definition?.productId ?? definition?.product?.id) === Number(item?.productId)
  ))?.product
);

const quantityLabel = (quantity, product) => {
  const value = Number(quantity || 0);
  if (!product?.pricePerWeight) return `${value}`;
  return `${value}${product?.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg'}`;
};

const makeLine = (data) => {
  const line = {
    ...data,
    printerNames: uniqueStrings(data.printerNames),
    courseIndex: Math.max(1, Math.trunc(Number(data.courseIndex || 1))),
    courseName: String(data.courseName || '').trim(),
    note: String(data.note || '').trim(),
  };
  line.printLine = appendPrintNote(formatCommandItem(line.quantityLabel, line.description), line.note);
  line.signature = JSON.stringify({
    quantityLabel: line.quantityLabel,
    description: line.description,
    note: line.note,
    printerNames: [...line.printerNames].sort(),
    courseIndex: line.courseIndex,
    courseName: line.courseName,
  });
  return line;
};

export const normalizeKitchenLines = (orders = [], { showFixedMenuName = false, roomId = null } = {}) => {
  const lines = [];
  (Array.isArray(orders) ? orders : []).forEach((order, orderIndex) => {
    const orderId = Number(order?.id);
    const orderCourse = orderIndex + 1;

    (order?.orderProducts || []).filter((row) => Number(row?.quantity || 0) > 0).forEach((row) => {
      const product = row?.product;
      const variants = (row?.orderProductVariants || [])
        .map((entry) => entry?.variant?.name || entry?.name)
        .filter(Boolean);
      lines.push(makeLine({
        key: `product:${row.id}`,
        orderId,
        courseIndex: orderCourse,
        quantityLabel: quantityLabel(row.quantity, product),
        description: `${row?.productNameSnapshot || product?.name || '-'}${variants.length ? ` + ${variants.join(' + ')}` : ''}`,
        note: row.note,
        printerNames: resolveProductPrinterTargets(product, roomId),
      }));
    });

    (order?.orderPackages || []).filter((row) => Number(row?.quantity || 0) > 0).forEach((row) => {
      const packageName = row?.package?.name || `Pacchetto ${row?.packageId || ''}`.trim();
      if (isTastingMenu(row)) {
        packageItems(row).forEach((item) => {
          const product = packageDefinitionProduct(row, item);
          const variantNames = Array.isArray(item?.variantNames) && item.variantNames.length > 0
            ? item.variantNames.filter(Boolean)
            : [item?.variantName || item?.variant?.name].filter(Boolean);
          lines.push(makeLine({
            key: `package-item:${item.id}`,
            orderId,
            courseIndex: Number(item?.courseIndex) || 1,
            courseName: item?.courseName,
            quantityLabel: `${Number(item?.quantity || 0) * Number(row.quantity || 0)}`,
            description: `${item?.productName || product?.name || '-'}${variantNames.length ? ` + ${variantNames.join(' + ')}` : ''} [${packageName}]`,
            note: [item?.note, row?.note].filter(Boolean).join(' / '),
            printerNames: resolveProductPrinterTargets(product, roomId),
          }));
        });
        return;
      }

      const targets = packageItems(row).flatMap((item) => resolveProductPrinterTargets(packageDefinitionProduct(row, item), roomId));
      lines.push(makeLine({
        key: `package:${row.id}`,
        orderId,
        courseIndex: orderCourse,
        quantityLabel: `${Number(row.quantity || 0)}`,
        description: `[Pacchetto] ${packageName}`,
        note: row.note,
        printerNames: targets,
      }));
    });

    (order?.orderFixedPriceMenus || []).forEach((menu) => {
      (menu?.selections || []).forEach((selection) => {
        const supplement = Number(selection?.extraPriceSnapshot || 0);
        lines.push(makeLine({
          key: `fixed:${selection.id}`,
          kind: 'fixed-price-menu-selection',
          orderId,
          courseIndex: Number(selection?.courseIndex) || 1,
          courseName: selection?.groupNameSnapshot,
          quantityValue: Number(selection?.quantity || 1) * Number(menu?.quantity || 1),
          quantityLabel: `${Number(selection?.quantity || 1) * Number(menu?.quantity || 1)}`,
          description: `${selection?.productNameSnapshot || selection?.product?.name || '-'}${supplement > 0 ? ` +EUR ${supplement.toFixed(2)}` : ''}${showFixedMenuName ? ` [${menu?.menuNameSnapshot || 'Menu'}]` : ''}`,
          note: [selection?.note, menu?.note].filter(Boolean).join(' / '),
          printerNames: resolveProductPrinterTargets(selection?.product, roomId),
        }));
      });
    });
  });
  return lines;
};

const cloneForPrinters = (line, names, displayLine) => ({
  ...line,
  printerNames: names,
  displayLine,
});

export const buildKitchenDiff = (currentLines, storedSnapshot) => {
  const previousLines = Array.isArray(storedSnapshot?.lines) ? storedSnapshot.lines : [];
  const transferDestinations = storedSnapshot?.transferDestinations
    && typeof storedSnapshot.transferDestinations === 'object'
    && !Array.isArray(storedSnapshot.transferDestinations)
    ? storedSnapshot.transferDestinations
    : {};
  const currentOrderIds = uniqueStrings(currentLines.map((line) => line.orderId));
  const previousOrderIds = uniqueStrings(storedSnapshot?.orderIds || previousLines.map((line) => line.orderId));
  const sameService = previousOrderIds.some((id) => currentOrderIds.includes(id));

  if (previousLines.length === 0 || !sameService) {
    return { firstPrint: true, lines: currentLines.map((line) => ({ ...line, displayLine: line.printLine })) };
  }

  const previousByKey = new Map(previousLines.map((line) => [line.key, line]));
  const currentByKey = new Map(currentLines.map((line) => [line.key, line]));
  const changes = [];

  previousLines.forEach((oldLine) => {
    const nextLine = currentByKey.get(oldLine.key);
    if (!nextLine) {
      const destination = String(transferDestinations[oldLine.key] || '').trim();
      const displayLine = destination
        ? `TRASFERITO AL TAVOLO ${destination}\r\n   ${oldLine.printLine}`
        : `CANCELLA ${oldLine.printLine}`;
      changes.push(cloneForPrinters(oldLine, oldLine.printerNames, displayLine));
      return;
    }
    if (oldLine.signature === nextLine.signature) return;

    const oldPrinters = uniqueStrings(oldLine.printerNames);
    const nextPrinters = uniqueStrings(nextLine.printerNames);
    const shared = oldPrinters.filter((name) => nextPrinters.includes(name));
    const removed = oldPrinters.filter((name) => !nextPrinters.includes(name));
    const added = nextPrinters.filter((name) => !oldPrinters.includes(name));
    if (shared.length > 0) {
      changes.push(cloneForPrinters(nextLine, shared, `MODIFICA\r\n   DA: ${oldLine.printLine}\r\n   A: ${nextLine.printLine}`));
    }
    if (removed.length > 0) changes.push(cloneForPrinters(oldLine, removed, `CANCELLA ${oldLine.printLine}`));
    if (added.length > 0) changes.push(cloneForPrinters(nextLine, added, `AGGIUNGI ${nextLine.printLine}`));
  });

  currentLines.forEach((line) => {
    if (!previousByKey.has(line.key)) {
      changes.push(cloneForPrinters(line, line.printerNames, `AGGIUNGI ${line.printLine}`));
    }
  });
  return { firstPrint: false, lines: changes };
};

export const buildKitchenTargets = (lines, {
  title = '',
  tableName,
  tableNote = '',
  customerLabel = '',
  isReprint = false,
  fontSize = 'NORMAL',
}) => {
  const grouped = new Map();
  const unassignedItems = [];

  const addCourseLine = (course, line) => {
    const displayed = line.displayLine || line.printLine;
    if (line.kind !== 'fixed-price-menu-selection') {
      course.lines.push(displayed);
      return;
    }

    const actionPrefix = displayed === line.printLine
      ? ''
      : ['AGGIUNGI ', 'CANCELLA '].find((prefix) => displayed === `${prefix}${line.printLine}`);
    if (actionPrefix === undefined) {
      course.lines.push(displayed);
      return;
    }

    const aggregateKey = JSON.stringify({
      actionPrefix,
      description: line.description,
      note: line.note,
    });
    const existingIndex = course.fixedMenuLineIndexes.get(aggregateKey);
    if (existingIndex === undefined) {
      course.fixedMenuLineIndexes.set(aggregateKey, course.lines.length);
      course.lines.push({
        actionPrefix,
        description: line.description,
        note: line.note,
        quantity: Number(line.quantityValue || line.quantityLabel || 0),
      });
      return;
    }

    course.lines[existingIndex].quantity += Number(line.quantityValue || line.quantityLabel || 0);
  };

  lines.forEach((line) => {
    if (!line.printerNames?.length) {
      unassignedItems.push(line.description);
      return;
    }
    line.printerNames.forEach((printerName) => {
      if (!grouped.has(printerName)) grouped.set(printerName, new Map());
      const courses = grouped.get(printerName);
      if (!courses.has(line.courseIndex)) {
        courses.set(line.courseIndex, { name: line.courseName, lines: [], fixedMenuLineIndexes: new Map() });
      }
      addCourseLine(courses.get(line.courseIndex), line);
    });
  });

  const targets = [...grouped.entries()].map(([printerName, courses]) => {
    const body = [];
    [...courses.entries()].sort(([a], [b]) => a - b).forEach(([index, group], position, all) => {
      body.push(formatCourseHeading(index, group.name));
      body.push(...group.lines.map((entry) => {
        if (typeof entry === 'string') return entry;
        const printLine = appendPrintNote(
          formatCommandItem(`${entry.quantity}`, entry.description),
          entry.note
        );
        return `${entry.actionPrefix}${printLine}`;
      }));
      body.push(formatCourseSeparator(position < all.length - 1));
    });
    return {
      printerName,
      content: {
        kind: 'KITCHEN_ORDER',
        title: isReprint ? 'TIPO STAMPA: RISTAMPA COMANDA' : title,
        tableName: tableName || '-',
        tableNote: String(tableNote || '').trim(),
        customerName: printableCustomer(customerLabel),
        fontSize: normalizeOrderPrintFontSize(fontSize),
        date: new Date().toLocaleString('it-IT', { hour12: false }),
        body: body.filter(Boolean).join('\r\n'),
      },
    };
  });
  return { targets, unassignedItems };
};

export const makeKitchenSnapshot = (lines) => ({
  version: 1,
  orderIds: [...new Set(lines.map((line) => line.orderId))],
  lines,
});
